import { IOrdersHook } from '@models';
import { OrderService } from '@services';
import { useStore } from '@stores';
import { getDateRangeFromPreset } from '@utils';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useOrders = (): IOrdersHook => {
  const { orderStore } = useStore();
  const { orders, totalCount, currentPage, skipCount, maxResultCount, loading, handlePageChange } =
    orderStore;
  const [service, setService] = useState<string>();
  const [customer, setCustomer] = useState<string>();
  const [registrationNumber, setRegistrationNumber] = useState<string>();
  const [status, setStatus] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<string | undefined>();
  const [createdRange, setCreatedRange] = useState<string | undefined>();

  const { startAt, endAt } = useMemo(() => getDateRangeFromPreset(dateRange), [dateRange]);
  const { startAt: createdFrom, endAt: createdTo } = useMemo(() => getDateRangeFromPreset(createdRange), [createdRange]);

  useEffect(() => {
    return () => orderStore.resetOrders();
  }, [orderStore]);

  useEffect(() => {
    orderStore.getAll({
      registrationNumber,
      status,
      service,
      customer,
      startAt,
      endAt,
      createdFrom,
      createdTo,
      maxResultCount,
      skipCount,
    });
  }, [orderStore, service, customer, registrationNumber, status, startAt, endAt, createdFrom, createdTo, skipCount, maxResultCount]);

  const handleServiceSearch = debounce((text: string) => {
    setService(text);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  }, 500);

  const handleCustomerSearch = debounce((text: string) => {
    setCustomer(text);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  }, 500);

  const handleRegistrationNumberSearch = debounce((text: string) => {
    setRegistrationNumber(text);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  }, 500);

  const handleStatusChange = (text: string) => {
    setStatus(text);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  };

  const handleDateRangeChange = (range: string) => {
    setDateRange(range || undefined);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  };

  const handleCreatedRangeChange = (range: string) => {
    setCreatedRange(range || undefined);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  };

  const fetchAllForExport = useCallback(async () => {
    const result = await OrderService.getAll({
      registrationNumber,
      status,
      service,
      customer,
      startAt,
      endAt,
      createdFrom,
      createdTo,
      maxResultCount: totalCount || 10000,
      skipCount: 0,
    });
    return result.items;
  }, [registrationNumber, status, service, customer, startAt, endAt, createdFrom, createdTo, totalCount]);

  return {
    loading,
    status,
    dateRange,
    createdRange,
    dataSource: Array.from(orders.values()),
    totalCount,
    skipCount,
    currentPage,
    maxResultCount,
    handleServiceSearch,
    handleCustomerSearch,
    handleRegistrationNumberSearch,
    handleStatusChange,
    handleDateRangeChange,
    handleCreatedRangeChange,
    handlePageChange,
    fetchAllForExport,
  };
};
